import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ChevronLeft, ChevronRight, Swords, Heart, Trophy, AlertTriangle, Users, Loader2, Send, Shield, FlaskConical, TrendingUp, Star } from "lucide-react";
import { CLASS_COLORS } from "@/lib/constants";
import { useTeams } from "@/lib/useTeams";
import { getWeeklyReport, sendWeeklyReportDiscord } from "@/lib/api";
import { toast } from "sonner";

function getWeekRange(date) {
  const d = new Date(date);
  const day = d.getDay();
  const diff = day === 0 ? -6 : 1 - day; // Monday start
  const monday = new Date(d);
  monday.setDate(d.getDate() + diff);
  const sunday = new Date(monday);
  sunday.setDate(monday.getDate() + 6);
  return {
    start: monday.toISOString().split("T")[0],
    end: sunday.toISOString().split("T")[0],
    monday,
    sunday,
  };
}

function formatDate(dateStr) {
  const d = new Date(dateStr + "T12:00:00");
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

const TEAM_COLORS = { Monday: "#3b82f6", Thursday: "#f59e0b" };

export default function WeeklyReport() {
  const teams = useTeams();
  const navigate = useNavigate();
  const [team, setTeam] = useState("Monday");
  const [weekOffset, setWeekOffset] = useState(0);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [webhookUrl, setWebhookUrl] = useState("");
  const [sending, setSending] = useState(false);

  const week = useMemo(() => {
    const now = new Date();
    now.setDate(now.getDate() + weekOffset * 7);
    return getWeekRange(now);
  }, [weekOffset]);

  const loadReport = async () => {
    setLoading(true);
    try {
      const data = await getWeeklyReport(team, week.start, week.end);
      setReport(data);
    } catch (e) {
      toast.error("Failed to load weekly report");
    }
    setLoading(false);
  };

  const handleSendDiscord = async () => {
    if (!webhookUrl.trim()) {
      toast.error("Enter a Discord webhook URL");
      return;
    }
    setSending(true);
    try {
      await sendWeeklyReportDiscord(webhookUrl.trim(), team, week.start, week.end);
      toast.success("Weekly report sent to Discord!");
    } catch {
      toast.error("Failed to send to Discord");
    }
    setSending(false);
  };

  return (
    <div className="p-6 md:p-8 lg:p-12 animate-fade-in" data-testid="weekly-report-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="heading text-4xl md:text-5xl font-bold tracking-tight uppercase">Weekly Report</h1>
          <p className="text-muted-foreground text-sm mt-1">Performance summary by week</p>
        </div>
        <Select value={team} onValueChange={setTeam}>
          <SelectTrigger data-testid="report-team-select" className="w-40 h-8 text-xs bg-card/50">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {teams.map((t) => <SelectItem key={t} value={t}>{t} Team</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {/* Week Picker */}
      <div className="flex items-center justify-center gap-4 mb-6">
        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setWeekOffset(weekOffset - 1)} data-testid="prev-week-btn">
          <ChevronLeft className="h-4 w-4" />
        </Button>
        <div className="text-center">
          <p className="text-lg font-bold font-mono tracking-tight" data-testid="week-range-label">
            {formatDate(week.start)} — {formatDate(week.end)}
          </p>
          <p className="text-xs text-muted-foreground font-mono">{week.start} to {week.end}</p>
        </div>
        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setWeekOffset(weekOffset + 1)} data-testid="next-week-btn">
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>

      {/* Generate Button */}
      <div className="flex justify-center mb-8">
        <Button data-testid="generate-report-btn" onClick={loadReport} disabled={loading}
          className="gap-2 bg-foreground text-background hover:bg-foreground/90 rounded-sm">
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <TrendingUp className="h-4 w-4" />}
          {loading ? "Generating..." : "Generate Report"}
        </Button>
      </div>

      {/* Report Content */}
      {report && (
        <div className="space-y-8" data-testid="report-content">
          {report.raid_count === 0 ? (
            <div className="text-center py-16 text-muted-foreground">
              <Users className="h-10 w-10 mx-auto mb-3 opacity-30" />
              <p className="text-sm">No raids found for <span className="font-bold" style={{ color: TEAM_COLORS[team] }}>{team}</span> team this week</p>
            </div>
          ) : (
            <>
              {/* Summary Stats */}
              <div>
                <SectionHeader icon={TrendingUp} label="Overview" color="#fff" />
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mt-3">
                  <StatCard label="Raids" value={report.raid_count} />
                  <StatCard label="Players" value={report.summary_stats.total_players} />
                  <StatCard label="Avg DPS" value={report.summary_stats.avg_dps?.toLocaleString()} />
                  <StatCard label="Avg HPS" value={report.summary_stats.avg_hps?.toLocaleString()} />
                  <StatCard label="Consumables" value={`${report.consumable_avg}%`}
                    color={report.consumable_avg >= 80 ? "text-green-500" : report.consumable_avg >= 50 ? "text-yellow-500" : "text-destructive"} />
                  <StatCard label="Total Deaths" value={report.summary_stats.total_deaths} color="text-destructive" />
                </div>
              </div>

              {/* Raids this week */}
              <div>
                <SectionHeader icon={Shield} label={`Raids (${report.raid_count})`} color="#888" />
                <div className="flex flex-wrap gap-2 mt-3">
                  {report.raids.map((r) => (
                    <button key={r.id} data-testid={`report-raid-${r.id}`}
                      onClick={() => navigate(`/raid/${r.id}`)}
                      className="px-3 py-1.5 rounded-sm border border-border/20 bg-card/30 hover:bg-card/50 transition-colors text-left">
                      <p className="text-sm font-medium" style={{ color: TEAM_COLORS[team] }}>{r.raid_name}</p>
                      <p className="text-[10px] text-muted-foreground font-mono">{r.date}</p>
                    </button>
                  ))}
                </div>
              </div>

              {/* MVP */}
              {report.mvp.length > 0 && (
                <div>
                  <SectionHeader icon={Trophy} label="MVP of the Week" color="#fbbf24" />
                  <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-3 mt-3">
                    {report.mvp.map((m, i) => {
                      const medals = ["#FFD700", "#C0C0C0", "#CD7F32"];
                      const medalEmoji = i === 0 ? "1st" : i === 1 ? "2nd" : i === 2 ? "3rd" : `${i + 1}th`;
                      return (
                        <div key={m.player_id} data-testid={`mvp-${m.player_name}`}
                          className={`p-3 rounded-sm border transition-all ${i < 3 ? "border-yellow-500/20 bg-yellow-950/10" : "border-border/10 bg-card/30"}`}>
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-xs font-bold font-mono" style={{ color: medals[i] || "#888" }}>#{i + 1}</span>
                            <span className="text-sm font-medium truncate" style={{ color: CLASS_COLORS[m.wow_class] || "#ccc" }}>
                              {m.player_name}
                            </span>
                          </div>
                          <div className="flex items-center gap-2 text-[10px] text-muted-foreground font-mono">
                            <span>{m.avg_score} pts</span>
                            <span>{m.raids} raid{m.raids !== 1 ? "s" : ""}</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Top DPS */}
              {report.top_dps.length > 0 && (
                <div>
                  <SectionHeader icon={Swords} label="Top DPS" color="#ff4444" />
                  <div className="space-y-1 mt-3">
                    {report.top_dps.map((d, i) => (
                      <RankRow key={d.player_id} rank={i + 1} name={d.player_name} wowClass={d.wow_class}
                        primary={`${d.avg_dps.toLocaleString()} avg`} secondary={`${d.best_dps.toLocaleString()} best`}
                        raids={d.raids} navigate={navigate} playerId={d.player_id} />
                    ))}
                  </div>
                </div>
              )}

              {/* Top HPS */}
              {report.top_hps.length > 0 && (
                <div>
                  <SectionHeader icon={Heart} label="Top HPS" color="#1eff00" />
                  <div className="space-y-1 mt-3">
                    {report.top_hps.map((h, i) => (
                      <RankRow key={h.player_id} rank={i + 1} name={h.player_name} wowClass={h.wow_class}
                        primary={`${h.avg_hps.toLocaleString()} avg`} secondary={`${h.best_hps.toLocaleString()} best`}
                        raids={h.raids} navigate={navigate} playerId={h.player_id} />
                    ))}
                  </div>
                </div>
              )}

              {/* Parse Highlights */}
              {report.parse_highlights.length > 0 && (
                <div>
                  <SectionHeader icon={Star} label="Parse Stars" color="#a335ee" />
                  <div className="flex flex-wrap gap-2 mt-3">
                    {report.parse_highlights.map((p) => (
                      <div key={p.player_id} className="flex items-center gap-2 px-3 py-1.5 rounded-sm border border-purple-500/20 bg-purple-950/10">
                        <span className="text-sm font-medium" style={{ color: CLASS_COLORS[p.wow_class] || "#ccc" }}>{p.player_name}</span>
                        <span className={`text-xs font-bold font-mono ${p.parse_pct >= 95 ? "text-orange-400" : "text-purple-400"}`}>
                          {p.parse_pct}%
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Attendance */}
              {report.attendance.length > 0 && (
                <div>
                  <SectionHeader icon={Users} label={`Attendance (${report.attendance.length} players)`} color="#64748b" />
                  <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-1.5 mt-3">
                    {report.attendance.map((a) => (
                      <div key={a.player_id} className="flex items-center justify-between px-2 py-1 rounded-sm bg-card/20 border border-border/5">
                        <span className="text-xs truncate" style={{ color: CLASS_COLORS[a.wow_class] || "#ccc" }}>
                          {a.player_name}
                        </span>
                        <span className={`text-[10px] font-mono font-bold ${a.pct === 100 ? "text-green-500" : a.pct >= 50 ? "text-yellow-500" : "text-muted-foreground"}`}>
                          {a.raids}/{report.raid_count}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Gear Issues */}
              {report.gear_issues.length > 0 && (
                <div>
                  <SectionHeader icon={AlertTriangle} label={`Gear Issues (${report.gear_issues.length})`} color="#ef4444" />
                  <div className="space-y-1 mt-3">
                    {report.gear_issues.slice(0, 10).map((g) => (
                      <div key={g.player_id} className="flex items-center justify-between px-3 py-2 rounded-sm bg-destructive/5 border border-destructive/10">
                        <span className="text-sm" style={{ color: CLASS_COLORS[g.wow_class] || "#ccc" }}>{g.player_name}</span>
                        <div className="flex gap-3 text-[10px] font-mono text-destructive">
                          {g.missing_enchants.length > 0 && <span>{g.missing_enchants.length} enchants</span>}
                          {g.missing_gems > 0 && <span>{g.missing_gems} gems</span>}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Discord Webhook */}
              <div className="mt-8 p-4 rounded-sm border border-border/20 bg-card/20">
                <div className="flex items-center gap-2 mb-3">
                  <Send className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm font-medium">Send to Discord</span>
                </div>
                <div className="flex gap-2">
                  <input
                    data-testid="discord-webhook-input"
                    type="text"
                    value={webhookUrl}
                    onChange={(e) => setWebhookUrl(e.target.value)}
                    placeholder="Discord webhook URL..."
                    className="flex-1 h-8 px-3 text-sm bg-background/50 border border-border/30 rounded-sm focus:border-foreground/30 focus:outline-none placeholder:text-muted-foreground/50"
                  />
                  <Button data-testid="send-discord-btn" size="sm" onClick={handleSendDiscord} disabled={sending}
                    className="gap-1.5 text-xs rounded-sm">
                    {sending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
                    {sending ? "Sending..." : "Send"}
                  </Button>
                </div>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

function SectionHeader({ icon: Icon, label, color }) {
  return (
    <div className="flex items-center gap-2">
      <Icon className="h-4 w-4" style={{ color }} />
      <span className="text-sm font-mono uppercase tracking-widest" style={{ color }}>{label}</span>
    </div>
  );
}

function StatCard({ label, value, color }) {
  return (
    <div className="stat-card">
      <span className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">{label}</span>
      <p className={`text-lg font-bold font-mono ${color || ""}`}>{value}</p>
    </div>
  );
}

function RankRow({ rank, name, wowClass, primary, secondary, raids, navigate, playerId }) {
  return (
    <button onClick={() => navigate(`/player/${playerId}`)}
      className="w-full flex items-center gap-3 px-3 py-2 rounded-sm bg-card/20 border border-border/5 hover:bg-card/40 transition-colors text-left">
      <span className="text-xs font-bold font-mono text-muted-foreground w-6">#{rank}</span>
      <span className="text-sm font-medium truncate flex-1" style={{ color: CLASS_COLORS[wowClass] || "#ccc" }}>{name}</span>
      <span className="text-xs font-mono text-foreground">{primary}</span>
      <span className="text-[10px] font-mono text-muted-foreground">{secondary}</span>
      <Badge variant="outline" className="text-[9px]">{raids}r</Badge>
    </button>
  );
}
